import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PaperPlaneRight, ChatCircleDots, ShieldWarning, X, Warning } from "@phosphor-icons/react";

/**
 * StudentProctorChat
 * Runs during a student's active exam. Polls proctor messages and:
 *  - Shows a modal for warnings requiring acknowledgement
 *  - Provides a collapsible chat panel to reply to the proctor
 *  - When status flips to submitted with reason 'auto_terminated_violations', notifies onAutoSubmit
 */
export default function StudentProctorChat({ attemptId, onAutoSubmit, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ messages: [], warning_count: 0, warning_limit: 3, status: "in_progress" });
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [warningModal, setWarningModal] = useState(null); // message
  const [ackedIds, setAckedIds] = useState(() => new Set());
  const [autoSubNotified, setAutoSubNotified] = useState(false);
  const autoSubNotifiedRef = useRef(false);
  useEffect(() => { autoSubNotifiedRef.current = autoSubNotified; }, [autoSubNotified]);
  const pollRef = useRef(null);
  const bottomRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const lastStatusRef = useRef("in_progress");

  // Keep latest callbacks in refs so the poll interval never uses stale closures.
  const onStatusChangeRef = useRef(onStatusChange);
  const onAutoSubmitRef = useRef(onAutoSubmit);
  useEffect(() => { onStatusChangeRef.current = onStatusChange; }, [onStatusChange]);
  useEffect(() => { onAutoSubmitRef.current = onAutoSubmit; }, [onAutoSubmit]);
  // Also keep `open` and `ackedIds` in refs so the poll can read the latest values
  // without waiting for the effect deps to re-arm the interval.
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);
  const ackedIdsRef = useRef(ackedIds);
  useEffect(() => { ackedIdsRef.current = ackedIds; }, [ackedIds]);

  const load = async () => {
    try {
      const r = await api.get(`/attempts/${attemptId}/messages`);
      const prevIds = seenIdsRef.current;
      const newMsgs = r.data.messages || [];
      const isFirstRun = prevIds.size === 0;
      let firstUnreadProctorMsg = null;
      for (const m of newMsgs) {
        if (prevIds.has(m.message_id)) continue;
        prevIds.add(m.message_id);
        const isFromProctorOrSystem =
          m.from_role === "proctor" || m.from_role === "institution_admin" || m.from_role === "system";
        // Any un-acked warning MUST be surfaced — including on page refresh, so a student
        // who reloads mid-exam never misses a pending warning.
        if (m.kind === "warning" && !ackedIdsRef.current.has(m.message_id)) {
          setWarningModal(m);
        }
        // Bump unread badge for any proctor/system message when the drawer is closed
        if (isFromProctorOrSystem && !openRef.current) {
          setUnread((u) => u + 1);
        }
        // Auto-open the chat drawer for ANY proctor/system message that the student
        // hasn't seen yet (on first load OR live delta). Track only the first one to
        // toast about so we don't spam N notifications for a batch.
        if (isFromProctorOrSystem && !openRef.current) {
          if (!firstUnreadProctorMsg) firstUnreadProctorMsg = m;
          setOpen(true);
        }
      }
      // On live delta only (not initial history), also toast + audio ping so the student
      // notices even if their tab is out of focus.
      if (!isFirstRun && firstUnreadProctorMsg) {
        const preview = (firstUnreadProctorMsg.message || "").slice(0, 90);
        toast.info(`Proctor: ${preview}${firstUnreadProctorMsg.message?.length > 90 ? "…" : ""}`, { duration: 6000 });
        try {
          // Best-effort audio cue — silently skipped if AudioContext isn't available/allowed.
          const AC = window.AudioContext || window.webkitAudioContext;
          if (AC) {
            const ctx = new AC();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.type = "sine"; o.frequency.value = 880;
            g.gain.setValueAtTime(0.001, ctx.currentTime);
            g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
            g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
            o.connect(g); g.connect(ctx.destination);
            o.start(); o.stop(ctx.currentTime + 0.28);
          }
        } catch { /* audio is best-effort */ }
      }
      setState(r.data);
      if (r.data.status && r.data.status !== lastStatusRef.current) {
        lastStatusRef.current = r.data.status;
        onStatusChangeRef.current?.(r.data.status, r.data);
      }
      if (r.data.status === "submitted" && r.data.submit_reason === "auto_terminated_violations" && !autoSubNotifiedRef.current) {
        autoSubNotifiedRef.current = true;
        setAutoSubNotified(true);
        toast.error("Your attempt was auto-submitted after 3 proctor warnings.", { duration: 10000 });
        onAutoSubmitRef.current?.();
      }
      if (r.data.status === "submitted" && r.data.submit_reason === "ended_by_proctor" && !autoSubNotifiedRef.current) {
        autoSubNotifiedRef.current = true;
        setAutoSubNotified(true);
        // Force the drawer open so the ⛔ system message and any final proctor note are visible.
        setOpen(true);
        const who = r.data.ended_by_name || "the proctor";
        const why = r.data.ended_reason ? ` — ${r.data.ended_reason}` : "";
        toast.error(`⛔ Exam ended by ${who}${why}. Your answers have been submitted.`, { duration: 10000 });
        onAutoSubmitRef.current?.();
      }
    } catch { /* polling — swallow */ }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 3000);
    return () => clearInterval(pollRef.current);
  }, [attemptId]);

  useEffect(() => {
    if (open) { setUnread(0); bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }
  }, [open, state.messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setSending(true);
    try {
      await api.post(`/attempts/${attemptId}/reply`, { message: text });
      setInput("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };

  const ackWarning = () => {
    if (warningModal) {
      setAckedIds((s) => new Set([...s, warningModal.message_id]));
    }
    setWarningModal(null);
  };

  const warnCount = state.warning_count || 0;
  const warnLimit = state.warning_limit || 3;

  return (
    <>
      {/* Floating chat toggle. On mobile lifted above the sticky exam action bar (~64px + safe area). */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-40 rounded-full w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center shadow-lg border-2 ${
          warnCount >= 2 ? "bg-red-600 border-red-700 text-white" : warnCount >= 1 ? "bg-amber-500 border-amber-600 text-white" : "bg-zinc-900 border-zinc-900 text-white hover:bg-zinc-800"
        }`}
        data-testid="student-chat-toggle"
        aria-label="Proctor chat"
      >
        <ChatCircleDots size={22} weight="fill" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">
            {unread}
          </span>
        )}
      </button>

      {/* Chat drawer */}
      {open && (
        <div className="fixed bottom-36 sm:bottom-24 right-4 sm:right-6 z-40 w-80 max-w-[calc(100vw-2rem)] h-[440px] max-h-[70vh] border border-zinc-300 shadow-2xl bg-white flex flex-col" data-testid="student-chat-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200 bg-zinc-900 text-white">
            <div>
              <div className="text-[9px] uppercase tracking-[0.25em] opacity-70">Proctor chat</div>
              <div className="text-xs font-semibold">Warnings: {warnCount}/{warnLimit}</div>
            </div>
            <button onClick={() => setOpen(false)} className="hover:opacity-70" data-testid="student-chat-close">
              <X size={16} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2 bg-zinc-50 space-y-2" data-testid="student-chat-messages">
            {state.messages.length === 0 ? (
              <div className="text-center text-xs text-zinc-500 py-6">No messages yet.</div>
            ) : state.messages.map((m) => {
              const isSelf = m.from_role === "student";
              const isWarn = m.kind === "warning";
              const isSystem = m.kind === "system";
              return (
                <div key={m.message_id} className={`flex ${isSelf ? "justify-end" : "justify-start"}`} data-testid={`student-msg-${m.message_id}`}>
                  <div className={`max-w-[85%] px-2.5 py-1.5 text-xs border ${
                    isWarn ? "bg-red-50 border-red-300 text-red-900"
                    : isSystem ? "bg-zinc-900 text-white border-zinc-900"
                    : isSelf ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white border-zinc-200"
                  }`}>
                    <div className="text-[9px] uppercase tracking-wider opacity-70 mb-0.5">
                      {m.from_name} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <div className="whitespace-pre-wrap">{m.message}</div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-zinc-200 p-2 flex gap-1.5">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
              placeholder="Reply to proctor…"
              className="flex-1 border border-zinc-300 px-2 py-1.5 text-xs focus:border-zinc-900 focus:outline-none"
              data-testid="student-chat-input"
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              className="bg-zinc-900 text-white px-3 disabled:opacity-50"
              data-testid="student-chat-send"
            >
              <PaperPlaneRight size={12} weight="fill" />
            </button>
          </div>
        </div>
      )}

      {/* Warning modal — must be acknowledged */}
      {warningModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="student-warning-modal">
          <div className="bg-white max-w-md w-full border-4 border-red-600">
            <div className="bg-red-600 text-white px-5 py-4 flex items-center gap-3">
              <ShieldWarning size={28} weight="fill" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-90">Formal warning</div>
                <div className="font-display text-xl font-bold">
                  {state.warning_count >= warnLimit ? "Final warning" : `Warning ${state.warning_count} of ${warnLimit}`}
                </div>
              </div>
            </div>
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">From {warningModal.from_name}</div>
              <div className="text-sm text-zinc-800 whitespace-pre-wrap border-l-2 border-red-500 pl-3">
                {warningModal.message}
              </div>
              {state.warning_count >= 2 && state.warning_count < warnLimit && (
                <div className="mt-4 text-xs text-red-700 font-semibold flex items-center gap-1.5">
                  <Warning size={12} weight="fill" /> One more warning will auto-submit your exam.
                </div>
              )}
              {state.warning_count >= warnLimit && (
                <div className="mt-4 text-xs text-red-700 font-semibold">
                  Your attempt has been auto-submitted.
                </div>
              )}
            </div>
            <div className="px-5 pb-5 flex justify-end">
              <button
                onClick={ackWarning}
                className="bg-red-600 text-white px-5 py-2 text-sm font-medium hover:bg-red-700"
                data-testid="student-warning-ack-btn"
              >
                I acknowledge
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
